import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import eventsApi from '../api/eventsApi';
import { useLanguage } from '../context/LanguageContext';
import { t } from '../translations/translations';
import { initScrollAnimations, cleanupScrollAnimations } from '../utils/scrollAnimations';
import '../styles/events.css';
import '../styles/events-mobile.css';
import '../styles/animations.css';

const Events = () => {
  const { language } = useLanguage();
  const [activeTab, setActiveTab] = useState('upcoming');
  const [expandedEvents, setExpandedEvents] = useState({});
  const [isMainEventExpanded, setIsMainEventExpanded] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 200);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Initialize scroll animations
  useEffect(() => {
    const scrollObserver = initScrollAnimations();
    return () => {
      cleanupScrollAnimations(scrollObserver);
    };
  }, []);


  // Load events from MongoDB (language-aware)
  useEffect(() => {
    const loadEvents = async () => {
      try {
        setLoading(true);
        const eventsData = await eventsApi.getAllEvents(language);
        console.log('📅 Events loaded:', eventsData.length);
        eventsData.forEach(event => {
          const eventDate = new Date(event.date);
          const now = new Date();
          console.log(`Event: ${event.title}`);
          console.log(`  Raw date from MongoDB: ${event.date}`);
          console.log(`  Parsed to ISO: ${eventDate.toISOString()}`);
          console.log(`  UTC Date: ${eventDate.getUTCDate()}`);
          console.log(`  UTC Month: ${eventDate.getUTCMonth() + 1}`);
          console.log(`  UTC Year: ${eventDate.getUTCFullYear()}`);
          console.log(`  Now: ${now.toISOString()}`);
          console.log(`  Is future: ${eventDate >= now}`);
        });
        setEvents(eventsData);
      } catch (err) {
        setError(err.message);
        console.error('Failed to load events:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadEvents();
  }, [language]); // Reload when language changes

  // Helper functions to format data from MongoDB
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    // Use UTC methods to avoid timezone conversion issues
    console.log('🔍 Date Debug:', {
      original: dateString,
      parsed: date.toISOString(),
      utcDate: date.getUTCDate(),
      utcMonth: date.getUTCMonth() + 1,
      localDate: date.getDate(),
      localMonth: date.getMonth() + 1
    });
    return {
      day: date.getUTCDate().toString().padStart(2, '0'),
      month: date.toLocaleDateString('en-US', { month: 'long', timeZone: 'UTC' }),
      fullDate: date
    };
  };

  const getEventStatus = (event) => {
    if (event.availableSeats === 0) return t('events.soldOut', language);
    if (event.availableSeats <= 10) return t('events.almostSoldOut', language);
    if (event.availableSeats <= 20) return t('events.limitedSpots', language);
    return t('events.available', language);
  };

  const formatPrice = (price, currency) => {
    return `${price} PLN`;
  };

  // Process events from MongoDB
  const upcomingEvents = events.filter(event => {
    const eventDate = new Date(event.date);
    const now = new Date();
    return eventDate >= now;
  }).map(event => ({
    id: event._id,
    date: formatDate(event.date).day,
    month: formatDate(event.date).month,
    title: event.title,
    description: event.shortDescription || event.description,
    status: getEventStatus(event),
    image: event.thumbnail || "/assets/default-event.jpg",
    // Debug: log thumbnail path
    _debugThumbnail: event.thumbnail,
    price: formatPrice(event.price, event.currency),
    capacity: event.capacity,
    availableSeats: event.availableSeats,
    dressCode: event.dressCode,
    inclusions: event.inclusions || [],
    specialFeatures: event.specialFeatures || [],
    schedule: event.schedule || [],
    fullEvent: event,
    isFallback: event._id && event._id.startsWith('fallback-') // Flag fallback events
  }));
  
  console.log('📊 Upcoming events count:', upcomingEvents.length);
  console.log('📊 Main event:', upcomingEvents[0]);
  console.log('🖼️ Main event image path:', upcomingEvents[0]?.image);
  console.log('🖼️ Main event thumbnail:', upcomingEvents[0]?._debugThumbnail);

  const pastEvents = events.filter(event => {
    const eventDate = new Date(event.date);
    const now = new Date();
    return eventDate < now;
  }).map(event => ({
    id: event._id,
    date: formatDate(event.date).day,
    month: formatDate(event.date).month,
    title: event.title,
    description: event.shortDescription || event.description,
    status: "Completed",
    image: event.thumbnail || "/assets/default-event.jpg",
    // Don't include price for past events
    capacity: event.capacity,
    dressCode: event.dressCode,
    inclusions: event.inclusions || [],
    specialFeatures: event.specialFeatures || [],
    schedule: event.schedule || [],
    fullEvent: event
  }));

  const toggleEventDetails = (eventId) => {
    setExpandedEvents(prev => ({
      ...prev,
      [eventId]: !prev[eventId]
    }));
  };

  const toggleMainEventDetails = () => {
    setIsMainEventExpanded(!isMainEventExpanded);
  };

  // Get the main upcoming event (first one)
  const mainEvent = upcomingEvents[0];
  // Get other upcoming events (rest of the list)
  const otherUpcomingEvents = upcomingEvents.slice(1);
  // Get past events
  // Force upcoming events on mobile (screen width <= 768px)
  const isMobile = window.innerWidth <= 768;
  const currentOtherEvents = (isMobile || activeTab === 'upcoming') ? otherUpcomingEvents : pastEvents;

  console.log('🔍 Active Tab:', activeTab);
  console.log('🔍 Current events count:', currentOtherEvents.length);

  // Show loading state
  if (loading) {
    return (
      <div className="events-page">
        <Header isScrolled={isScrolled} />
        <section className="events-hero" style={{
          background: 'linear-gradient(rgba(21, 21, 20, 0.4), rgba(21, 21, 20, 0.4)), url(/assets/Garbary22.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}>
          <div className="events-hero-content">
            <h1 className="events-hero-title">Events</h1>
            <p className="events-hero-subtitle">Loading events...</p>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="events-page">
        <Header isScrolled={isScrolled} />
        <section className="events-hero" style={{
          background: 'linear-gradient(rgba(21, 21, 20, 0.4), rgba(21, 21, 20, 0.4)), url(/assets/Garbary22.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}>
          <div className="events-hero-content">
            <h1 className="events-hero-title">Events</h1>
            <p className="events-hero-subtitle">{error}</p>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  // Show "No Events" only after loading is complete
  if (!loading && !mainEvent) {
  return (
    <div className="events-page">
      <Header isScrolled={isScrolled} />
      
      <section 
        className="events-hero"
        style={{
          background: 'linear-gradient(rgba(21, 21, 20, 0.4), rgba(21, 21, 20, 0.4)), url(/assets/Garbary22.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="events-hero-content">
          <h1 className="events-hero-title">{t('events.title', language)}</h1>
          <p className="events-hero-subtitle">{t('events.hero.subtitle', language)}</p>
        </div>
      </section>

        <section className="events-list-section">
          <div className="events-list-content">
            <div className="events-list-header">
              <h2 className="events-list-title">{t('events.title', language)}</h2>
              <div className="events-list-tabs">
                <button 
                  className={`events-list-tab ${activeTab === 'upcoming' ? 'events-list-tab-active' : ''}`}
                  onClick={() => setActiveTab('upcoming')}
                >
                  {t('events.upcoming', language)}
                </button>
                <button 
                  className={`events-list-tab ${activeTab === 'past' ? 'events-list-tab-active' : ''}`}
                  onClick={() => setActiveTab('past')}
                >
                  {t('events.past', language)}
                </button>
              </div>
            </div>
            
            <div className="events-list-grid">
              {currentOtherEvents.length === 0 ? (
                <div className="no-events">
                  <p>{t('events.noEvents', language)}</p>
                </div>
              ) : (
                currentOtherEvents.map((event) => (
                  <div key={event.id} className="events-list-item">
                    <div className="events-list-item-main">
                      <div className="events-list-item-image">
                        <img src={event.image} alt={event.title} className="events-list-item-img" />
                        <div className="events-list-item-status">{event.status}</div>
                      </div>
                      <div className="events-list-item-content">
                        <div className="events-list-item-date">
                          <span className="events-list-item-day">{event.date}</span>
                          <span className="events-list-item-month">{event.month}</span>
                        </div>
                        <div className="events-list-item-details">
                          <h3 className="events-list-item-title">{event.title}</h3>
                          <p className="events-list-item-description">{event.description}</p>
                          <div className="events-list-item-meta">
                            {activeTab === 'upcoming' && (
                              <>
                                <span className="events-list-item-time">{new Date(event.fullEvent?.date).toLocaleTimeString('en-US', { 
                                  hour: '2-digit', 
                                  minute: '2-digit',
                                  hour12: true 
                                })}</span>
                                <span className="events-list-item-price">{event.price}</span>
                              </>
                            )}
                          </div>
                          <div className="events-list-item-actions">
                            {activeTab === 'upcoming' && (
                              event.isFallback ? (
                                <button className="events-list-item-button" disabled>
                                  N/A
                                </button>
                              ) : event.status === t('events.soldOut', language) ? (
                                <button className="events-list-item-button" disabled>
                                  {t('events.soldOut', language)}
                                </button>
                              ) : (
                                <Link to={`/cart?event=${event.id}`} className="events-list-item-button">
                                  {t('events.getTickets', language)}
                                </Link>
                              )
                            )}
                            <span 
                              className="events-list-item-dropdown"
                              onClick={() => toggleEventDetails(event.id)}
                            >
                              {t('events.viewDetails', language)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className={`events-list-item-expanded ${expandedEvents[event.id] ? 'expanded' : 'collapsed'}`}>
                      <div className="events-list-item-expanded-content">
                        {event.schedule && event.schedule.length > 0 && (
                          <div className="events-list-item-expanded-section">
                            <h4>{t('events.schedule', language)}</h4>
                            {event.schedule.map((item, index) => (
                              <p key={index}>
                                <strong>{new Date(item.time).toLocaleTimeString('en-US', { 
                                  hour: '2-digit', 
                                  minute: '2-digit',
                                  hour12: true 
                                })}</strong> - {item.activity}
                              </p>
                            ))}
                          </div>
                        )}
                        <div className="events-list-item-expanded-section">
                          <h4>{t('events.details', language)}</h4>
                          {event.dressCode && (
                            <p><strong>{t('events.dressCode', language)}:</strong> {event.dressCode}</p>
                          )}
                          <p><strong>{t('events.duration', language)}:</strong> {event.fullEvent?.durationMinutes || 'TBD'} {language === 'pl' ? 'minut' : 'minutes'}</p>
                          <p><strong>{t('events.capacity', language)}:</strong> {event.capacity || 'TBD'} {language === 'pl' ? 'gości' : 'guests'}</p>
                          {activeTab === 'upcoming' && (
                            <p><strong>{t('events.availableSeats', language)}:</strong> {event.availableSeats || 'TBD'}</p>
                          )}
                          {event.inclusions && event.inclusions.length > 0 && (
                            <p><strong>{t('events.inclusions', language)}:</strong> {event.inclusions.join(', ')}</p>
                          )}
                          {event.specialFeatures && event.specialFeatures.length > 0 && (
                            <p><strong>{t('events.specialFeatures', language)}:</strong> {event.specialFeatures.join(', ')}</p>
                          )}
                        </div>
                        <div className="events-list-item-expanded-section">
                          <h4>{t('events.location', language)}</h4>
                          <p><strong>{t('events.venue', language)}:</strong> Pawilon Krzyżanowskiego</p>
                          <p><strong>{t('events.address', language)}:</strong> Ul. Garbary 96, 61-757 Poznań, {language === 'pl' ? 'Polska' : 'Poland'}</p>
                          <p><strong>{t('nav.contact', language)}:</strong> +48 532 883 067</p>
                          <p><strong>Email:</strong> Garbary96@gmail.com</p>
                          <p><strong>{language === 'pl' ? 'Parking' : 'Parking'}:</strong> {language === 'pl' ? 'Brak parkingu na miejscu - Płatny parking w pobliżu' : 'No Parking On Site - Paid Parking Nearby'}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>

        <section className="events-cta">
          <div className="events-cta-content">
            <h2 className="events-cta-title">{language === 'pl' ? 'Zorganizuj Swoje Wydarzenie U Nas' : 'Host Your Event With Us'}</h2>
            <p className="events-cta-description">
              {language === 'pl' ? 'Pawilon Krzyżanowskiego oferuje wyjątkowe miejsce na prywatne uroczystości, wydarzenia korporacyjne i specjalne okazje. Skontaktuj się z nami, aby omówić potrzeby swojego wydarzenia.' : 'Pawilon Krzyżanowskiego offers a unique venue for private celebrations, corporate events, and special occasions. Contact us to discuss your event needs.'}
            </p>
            <Link to="/contact" className="events-cta-button">{t('private.contact.button', language)}</Link>
          </div>
        </section>

        <Footer />
      </div>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <div className="events-page">
        <Header isScrolled={isScrolled} />
        <div className="events-loading">
          <h2>{language === 'pl' ? 'Ładowanie wydarzeń...' : 'Loading events...'}</h2>
          <p>{language === 'pl' ? 'Proszę czekać, pobieramy najnowsze wydarzenia.' : 'Please wait while we fetch the latest events.'}</p>
        </div>
        <Footer />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="events-page">
        <Header isScrolled={isScrolled} />
        <div className="events-error">
          <h2>{t('events.loadingError', language)}</h2>
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>{t('events.tryAgain', language)}</button>
        </div>
        <Footer />
      </div>
    );
  }

  // Show no events state
  if (events.length === 0) {
    return (
      <div className="events-page">
        <Header isScrolled={isScrolled} />
        <div className="events-empty">
          <h2>{t('events.noEvents', language)}</h2>
          <p>{language === 'pl' ? 'Wkrótce sprawdź nadchodzące wydarzenia w Pawilon Krzyżanowskiego.' : 'Check back soon for upcoming events at Pawilon Krzyżanowskiego.'}</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="events-page">
      <Header isScrolled={isScrolled} />
      
      <section 
        className="events-hero"
        style={{
          background: 'linear-gradient(rgba(21, 21, 20, 0.4), rgba(21, 21, 20, 0.4)), url(/assets/Garbary22.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="events-hero-content">
          <h1 className="events-hero-title">{t('events.title', language)}</h1>
          <p className="events-hero-subtitle">{t('events.subtitle', language)}</p>
        </div>
      </section>

      {/* Main Event Section - 50/50 */}
      <section className="events-main-hero">
        <div className="events-main-hero-content">
          <div className="events-main-hero-left">
            <img src={mainEvent?.image || "/assets/default-event.jpg"} alt={mainEvent?.title || "Event"} className="events-main-hero-image" />
            {mainEvent?.fullEvent?.tickets?.available === 'yes' && (
              <div className="events-main-hero-status">{mainEvent?.status || "Available"}</div>
            )}
          </div>
          <div className="events-main-hero-right">
            <div className="events-main-hero-info">
              {mainEvent?.fullEvent?.tickets?.available !== 'venue' && (
                <div className="events-main-hero-date">
                  <span className="events-main-hero-day">{mainEvent?.date || "TBD"}</span>
                  <span className="events-main-hero-month">{mainEvent?.month || "TBD"}</span>
                </div>
              )}
              <h1 className="events-main-hero-title">{mainEvent?.title || "Event Title"}</h1>
              <p className="events-main-hero-description">{mainEvent?.description || "Event description"}</p>
              {mainEvent?.fullEvent?.tickets?.available === 'yes' && (
                <div className="events-main-hero-price">
                  <span className="events-main-hero-price-label">{language === 'pl' ? 'Cena od:' : 'Starting From:'}</span>
                  <span className="events-main-hero-price-value">{mainEvent?.price || 'TBD'}</span>
                </div>
              )}
              <div className="events-main-hero-buttons">
                {mainEvent?.isFallback ? (
                  <button className="events-main-hero-button" disabled>
                    N/A
                  </button>
                ) : mainEvent?.fullEvent?.tickets?.available === 'venue' ? (
                  <Link to="/contact" className="events-main-hero-button">{t('events.contactUs', language)}</Link>
                ) : mainEvent?.fullEvent?.tickets?.available === 'no' ? (
                  <button className="events-main-hero-button" disabled style={{ opacity: 0.7 }}>
                    {t('events.freeEntry', language)}
                  </button>
                ) : mainEvent?.status === t('events.soldOut', language) ? (
                  <button className="events-main-hero-button" disabled>
                    {t('events.soldOut', language)}
                  </button>
                ) : (
                  <Link to={`/cart?event=${mainEvent?.id || '1'}`} className="events-main-hero-button">{t('events.getTickets', language)}</Link>
                )}
                <span 
                  className="events-main-hero-dropdown" 
                  onClick={toggleMainEventDetails}
                >
                  {t('events.viewDetails', language)}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className={`events-main-hero-expanded ${isMainEventExpanded ? 'expanded' : 'collapsed'}`}>
          <div className="events-main-hero-expanded-content">
            {mainEvent?.schedule && mainEvent.schedule.length > 0 && (
            <div className="events-list-item-expanded-section">
              <h4>{t('events.schedule', language)}</h4>
                {mainEvent.schedule.map((item, index) => (
                  <p key={index}>
                    <strong>{new Date(item.time).toLocaleTimeString('en-US', { 
                      hour: '2-digit', 
                      minute: '2-digit',
                      hour12: false 
                    })}</strong> - {item.activity}
                  </p>
                ))}
            </div>
            )}
            <div className="events-list-item-expanded-section">
              <h4>{t('events.details', language)}</h4>
              {mainEvent?.fullEvent?.tickets?.available === 'yes' && mainEvent?.dressCode && (
                <p><strong>{t('events.dressCode', language)}:</strong> {mainEvent.dressCode}</p>
              )}
              {mainEvent?.fullEvent?.tickets?.available !== 'venue' && (
                <p><strong>{t('events.duration', language)}:</strong> {mainEvent?.fullEvent?.durationMinutes || 'TBD'} {language === 'pl' ? 'minut' : 'minutes'}</p>
              )}
              {mainEvent?.fullEvent?.tickets?.available === 'yes' && (
                <>
                  <p><strong>{t('events.capacity', language)}:</strong> {mainEvent?.capacity || 'TBD'} {language === 'pl' ? 'gości' : 'guests'}</p>
                  <p><strong>{t('events.availableSeats', language)}:</strong> {mainEvent?.availableSeats || 'TBD'}</p>
                </>
              )}
              {mainEvent?.inclusions && mainEvent.inclusions.length > 0 && (
                <p>{(mainEvent.fullEvent?.tickets?.available === 'yes') && <strong>{mainEvent.inclusions && mainEvent.inclusions.length > 0 && mainEvent.inclusions.some(item => item.includes('Canapés') || item.includes('Main course') || item.includes('soup') || item.includes('course:')) ? (language === 'pl' ? 'Menu:' : 'Menu:') : t('events.inclusions', language)}: </strong>}{mainEvent.inclusions.join(', ')}</p>
              )}
              {mainEvent?.specialFeatures && mainEvent.specialFeatures.length > 0 && (
                <p><strong>{t('events.specialFeatures', language)}:</strong> {mainEvent.specialFeatures.join(', ')}</p>
              )}
            </div>
            <div className="events-list-item-expanded-section">
              <h4>{t('events.location', language)}</h4>
              <p><strong>{t('events.venue', language)}:</strong> Pawilon Krzyżanowskiego</p>
              <p><strong>{t('events.address', language)}:</strong> Ul. Garbary 96, 61-757 Poznań, {language === 'pl' ? 'Polska' : 'Poland'}</p>
              <p><strong>{t('nav.contact', language)}:</strong> +48 532 883 067</p>
              <p><strong>Email:</strong> Garbary96@gmail.com</p>
              <p><strong>Parking:</strong> {language === 'pl' ? 'Brak parkingu na miejscu - Płatny parking w pobliżu' : 'No Parking On Site - Paid Parking Nearby'}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Other Events List Section */}
      <section className="events-list-section">
        <div className="events-list-content">
          <div className="events-list-header">
            <h2 className="events-list-title">{language === 'pl' ? 'Inne Wydarzenia' : 'Other Events'}</h2>
            <div className="events-list-tabs">
              <button 
                className={`events-list-tab ${activeTab === 'upcoming' ? 'events-list-tab-active' : ''}`}
                onClick={() => {
                  setActiveTab('upcoming');
                  setExpandedEvents({});
                }}
              >
                {t('events.upcoming', language)}
              </button>
              <button 
                className={`events-list-tab ${activeTab === 'past' ? 'events-list-tab-active' : ''}`}
                onClick={() => {
                  setActiveTab('past');
                  setExpandedEvents({});
                }}
              >
                {t('events.past', language)}
              </button>
            </div>
          </div>
          
          <div className="events-list-grid">
            {currentOtherEvents.length === 0 ? (
              <div className="no-events">
                <p>{t('events.noEvents', language)}</p>
              </div>
            ) : (
              currentOtherEvents.map((event) => (
              <div key={event.id} className="events-list-item">
                <div className="events-list-item-main events-list-item-main-mobile">
                  <div className="events-list-item-image events-list-item-image-mobile">
                    <img src={event.image} alt={event.title} className="events-list-item-img" />
                     {event.fullEvent?.tickets?.available === 'yes' && (
                       <div className="events-list-item-status">{event.status}</div>
                     )}
                  </div>
                  <div className="events-list-item-content events-list-item-content-mobile">
                    {event.fullEvent?.tickets?.available !== 'venue' && (
                      <div className="events-list-item-date events-list-item-date-mobile">
                        <span className="events-list-item-day">{event.date}</span>
                        <span className="events-list-item-month">{event.month}</span>
                      </div>
                    )}
                    <div className="events-list-item-details events-list-item-details-mobile">
                      <h3 className="events-list-item-title events-list-item-title-mobile">{event.title}</h3>
                      <p className="events-list-item-description events-list-item-description-mobile">{event.description}</p>
                      <div className="events-list-item-meta events-list-item-meta-mobile">
                        {activeTab === 'upcoming' && event.fullEvent?.tickets?.available === 'yes' && !event.schedule && (
                          <>
                            <span className="events-list-item-time">{new Date(event.fullEvent?.date).toLocaleTimeString('en-US', { 
                              hour: '2-digit', 
                              minute: '2-digit',
                              hour12: true 
                            })}</span>
                            <span className="events-list-item-price">{event.price}</span>
                          </>
                        )}
                        {activeTab === 'upcoming' && event.fullEvent?.tickets?.available === 'yes' && event.schedule && (
                          <span className="events-list-item-price">{event.price}</span>
                        )}
                      </div>
                      <div className="events-list-item-actions events-list-item-actions-mobile">
                        {activeTab === 'upcoming' ? (
                          event.fullEvent?.tickets?.available === 'venue' ? (
                            <Link to="/contact" className="events-list-item-button">
                              {t('events.contactUs', language)}
                            </Link>
                          ) : event.fullEvent?.tickets?.available === 'no' ? (
                            <button className="events-list-item-button" disabled style={{ opacity: 0.7 }}>
                              {t('events.freeEntry', language)}
                            </button>
                          ) : event.status === t('events.soldOut', language) ? (
                            <button className="events-list-item-button" disabled>
                              {t('events.soldOut', language)}
                            </button>
                          ) : (
                            <Link to={`/cart?event=${event.id}`} className="events-list-item-button">
                              {t('events.getTickets', language)}
                            </Link>
                          )
                        ) : null}
                        <span 
                          className={`events-list-item-dropdown ${activeTab === 'past' ? 'past-event-dropdown' : ''}`}
                          onClick={() => toggleEventDetails(event.id)}
                        >
                          {t('events.viewDetails', language)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className={`events-list-item-expanded ${expandedEvents[event.id] ? 'expanded' : 'collapsed'}`}>
                  <div className="events-list-item-expanded-content">
                    {event.schedule && event.schedule.length > 0 && (
                    <div className="events-list-item-expanded-section">
                      <h4>{t('events.schedule', language)}</h4>
                        {event.schedule.map((item, index) => (
                          <p key={index}>
                            <strong>{new Date(item.time).toLocaleTimeString('en-US', { 
                              hour: '2-digit', 
                              minute: '2-digit',
                              hour12: true 
                            })}</strong> - {item.activity}
                          </p>
                        ))}
                    </div>
                    )}
                    <div className="events-list-item-expanded-section">
                      <h4>{t('events.details', language)}</h4>
                      {event.fullEvent?.tickets?.available === 'yes' && event.dressCode && (
                        <p><strong>{t('events.dressCode', language)}:</strong> {event.dressCode}</p>
                      )}
                      {event.fullEvent?.tickets?.available !== 'venue' && (
                        <p><strong>{t('events.duration', language)}:</strong> {event.fullEvent?.durationMinutes || 'TBD'} {language === 'pl' ? 'minut' : 'minutes'}</p>
                      )}
                      {event.fullEvent?.tickets?.available === 'yes' && (
                        <>
                          <p><strong>{t('events.capacity', language)}:</strong> {event.capacity || 'TBD'} {language === 'pl' ? 'gości' : 'guests'}</p>
                          {activeTab === 'upcoming' && (
                            <p><strong>{t('events.availableSeats', language)}:</strong> {event.availableSeats || 'TBD'}</p>
                          )}
                        </>
                      )}
                      {event.inclusions && event.inclusions.length > 0 && (
                        <p>{(event.fullEvent?.tickets?.available === 'yes') && <strong>{event.inclusions && event.inclusions.length > 0 && event.inclusions.some(item => item.includes('Canapés') || item.includes('Main course') || item.includes('soup') || item.includes('course:')) ? (language === 'pl' ? 'Menu:' : 'Menu:') : t('events.inclusions', language)}: </strong>}{event.inclusions.join(', ')}</p>
                      )}
                      {event.specialFeatures && event.specialFeatures.length > 0 && (
                        <p><strong>{t('events.specialFeatures', language)}:</strong> {event.specialFeatures.join(', ')}</p>
                      )}
                    </div>
                    <div className="events-list-item-expanded-section">
                      <h4>{t('events.location', language)}</h4>
                      <p><strong>{t('events.venue', language)}:</strong> Pawilon Krzyżanowskiego</p>
                      <p><strong>{t('events.address', language)}:</strong> Ul. Garbary 96, 61-757 Poznań, {language === 'pl' ? 'Polska' : 'Poland'}</p>
                      <p><strong>{t('nav.contact', language)}:</strong> +48 532 883 067</p>
                      <p><strong>Email:</strong> Garbary96@gmail.com</p>
                      <p><strong>Parking:</strong> {language === 'pl' ? 'Brak parkingu na miejscu - Płatny parking w pobliżu' : 'No Parking On Site - Paid Parking Nearby'}</p>
                    </div>
                  </div>
                </div>
              </div>
              ))
            )}
          </div>
        </div>
      </section>

      <section className="events-cta">
        <div className="events-cta-content">
          <h2 className="events-cta-title">{language === 'pl' ? 'Zorganizuj Swoje Wydarzenie U Nas' : 'Host Your Event With Us'}</h2>
          <p className="events-cta-description">
            {language === 'pl' ? 'Pawilon Krzyżanowskiego oferuje wyjątkowe miejsce na prywatne uroczystości, wydarzenia korporacyjne i specjalne okazje. Skontaktuj się z nami, aby omówić potrzeby swojego wydarzenia.' : 'Pawilon Krzyżanowskiego offers a unique venue for private celebrations, corporate events, and special occasions. Contact us to discuss your event needs.'}
          </p>
          <Link to="/contact" className="events-cta-button">{t('private.contact.button', language)}</Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Events;
